import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { setUsers } from '../../store/adminSlice';
import { fmtUSD } from '../../utils/formatters';
import { adminApi } from '../../services/api';

const UsersManagement = ({ onlineUsers = [] }) => {
  const dispatch = useDispatch();
  const { users, adminPermissions, adminUser } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState('');
  const [convertingLead, setConvertingLead] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');
  const [deletePin, setDeletePin] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);

  const userList = Array.isArray(users) ? users : [];
  const isSuperAdmin = adminUser?.role === 'super_admin' || adminUser?.role === 'admin';

  const filteredUsers = userList.filter(user => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (user.firstName?.toLowerCase().includes(s) || user.lastName?.toLowerCase().includes(s) || user.email?.toLowerCase().includes(s) || user.phone?.includes(s));
  });

  const fetchUsers = async () => {
    setIsRefreshing(true);
    try {
      const res = await adminApi.get('/admin/users');
      if (res.data?.users) {
        dispatch(setUsers(res.data.users));
      }
    } catch (e) {
      console.error('Failed to refresh users:', e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusBadge = (user) => {
    if (user.isSuspended) return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b91c1c]">SUSPENDED</span>;
    if (user.isVerified) return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">VERIFIED</span>;
    if (user.documentsSubmitted) return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b45309]">PENDING KYC</span>;
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#94a3b8] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#64748b]">UNVERIFIED</span>;
  };

  const isOnline = (userId) => onlineUsers.includes(userId);

  const handleConvertToLead = async (userId) => {
    if (convertingLead[userId]) return;
    setConvertingLead(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await adminApi.post(`/admin/users/${userId}/convert-to-lead`);
      dispatch(showToast({ message: res.data?.message || 'Lead created successfully', type: 'success' }));
    } catch (e) {
      dispatch(showToast({ message: e.response?.data?.message || 'Failed to convert to lead', type: 'error' }));
    } finally {
      setConvertingLead(prev => ({ ...prev, [userId]: false }));
    }
  };

  // ─── DELETE USER HANDLERS ──────────────────────────────────────────

  const openDeleteModal = (userId, userName) => {
    setDeleteUserId(userId);
    setDeleteUserName(userName);
    setDeletePin('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteUserId(null);
    setDeleteUserName('');
    setDeletePin('');
    setDeletingUser(false);
  };

  const handleDeleteUser = async () => {
    if (deletePin !== '1861') {
      dispatch(showToast({ message: 'Incorrect security PIN!', type: 'error' }));
      return;
    }

    setDeletingUser(true);
    try {
      await adminApi.delete(`/admin/users/${deleteUserId}`);
      dispatch(showToast({ message: 'User deleted successfully!', type: 'success' }));
      closeDeleteModal();
      fetchUsers();
    } catch (e) {
      dispatch(showToast({ message: e.response?.data?.message || 'Failed to delete user', type: 'error' }));
    } finally {
      setDeletingUser(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">USER MANAGEMENT</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1">
            <span className="text-[#1e3a5f]">{filteredUsers.length}</span> of {userList.length} users | 
            <span className="text-[#059669] ml-2"><i className="fas fa-circle text-[6px] mr-1" />{onlineUsers.length} ONLINE</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-xs" />
            <input 
              type="text" 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              placeholder="Search users..." 
              className="pl-10 pr-4 py-2.5 bg-white border-2 border-[#cbd5e1] rounded-xl text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all w-64 placeholder:text-[#94a3b8]" 
            />
          </div>
          <button 
            onClick={fetchUsers} 
            disabled={isRefreshing}
            className="px-4 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 transition-all border-b-[2px] border-[#0b1e33] flex items-center gap-2"
          >
            {isRefreshing ? (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="fas fa-sync-alt" />
            )}
            {isRefreshing ? 'REFRESHING...' : 'REFRESH'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0]">
              {['USER', 'EMAIL', 'BALANCE', 'DEPOSITS', 'STATUS', 'ONLINE', 'JOINED', 'ACTIONS'].map(h => (
                <th key={h} className="text-left px-5 py-3.5 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-users-slash text-[#94a3b8] text-2xl" />
                  </div>
                  <p className="text-[#475569] font-syne font-extrabold text-sm uppercase tracking-wider">
                    {userList.length === 0 ? 'No users registered yet' : 'No users match'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                  {/* User */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white border-2 border-[#e2e8f0] flex items-center justify-center flex-shrink-0 shadow-md">
  <i className="fas fa-user text-sm text-black" />
</div>
                      <div>
                        <div className="font-syne text-sm font-extrabold text-[#0f172a] whitespace-nowrap">{user.firstName} {user.lastName}</div>
                        <div className="text-[9px] text-[#64748b] font-mono font-bold truncate max-w-[100px]">{user.id?.slice(0,8)}…</div>
                      </div>
                    </div>
                  </td>
                  {/* Email */}
                  <td className="px-5 py-3.5 text-[12px] text-[#475569] font-bold max-w-[160px] truncate">{user.email}</td>
                  {/* Balance */}
                  <td className="px-5 py-3.5 font-mono text-[12px] font-extrabold text-[#059669]">{fmtUSD(user.balance||0)}</td>
                  {/* Deposits */}
                  <td className="px-5 py-3.5 font-mono text-[12px] font-extrabold text-[#1e3a5f]">{fmtUSD(user.totalDeposits||0)}</td>
                  {/* Status */}
                  <td className="px-5 py-3.5">{getStatusBadge(user)}</td>
                  {/* Online */}
                  <td className="px-5 py-3.5">
                    {isOnline(user.id) ? (
                      <span className="inline-flex items-center gap-1.5 bg-[#ecfdf5] border-2 border-[#a7f3d0] text-[#059669] text-[10px] font-mono font-extrabold px-2.5 py-1 rounded-lg uppercase tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-[#059669]" /> ONLINE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#94a3b8] text-[10px] font-mono font-extrabold px-2.5 py-1 rounded-lg uppercase tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-[#cbd5e1]" /> OFFLINE
                      </span>
                    )}
                  </td>
                  {/* Joined */}
                  <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  {/* Actions */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => window.open(`/admin/user/${user.id}`, '_blank')} 
                        className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider transition-all hover:bg-[#0b1e33] border-b-[2px] border-[#0b1e33]"
                      >
                        <i className="fas fa-external-link-alt mr-1.5" /> VIEW DETAILS
                      </button>
                      <button 
                        onClick={() => handleConvertToLead(user.id)}
                        disabled={convertingLead[user.id]}
                        className="px-3 py-1.5 rounded-lg bg-[#d97706] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider transition-all hover:bg-[#b45309] disabled:opacity-50 border-b-[2px] border-[#b45309]"
                      >
                        {convertingLead[user.id] ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <><i className="fas fa-user-plus mr-1.5" /> TO LEAD</>
                        )}
                      </button>
                      {isSuperAdmin && (
                        <button 
                          onClick={() => openDeleteModal(user.id, `${user.firstName} ${user.lastName}`)}
                          className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider transition-all hover:bg-[#b91c1c] border-b-[2px] border-[#b91c1c]"
                        >
                          <i className="fas fa-trash-alt mr-1.5" /> DELETE
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete User Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-[#e2e8f0] w-full max-w-2xl overflow-hidden" style={{ width: '50%', minWidth: '500px' }}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#0b1e33] to-[#1e3a5f] px-6 py-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 border-2 border-white/20 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-trash-alt text-white text-lg" />
              </div>
              <div>
                <h3 className="font-syne text-lg font-extrabold text-white uppercase tracking-tight">DELETE USER</h3>
                <p className="text-[11px] text-white/70 font-mono font-bold mt-0.5 uppercase tracking-wider">This action cannot be undone</p>
              </div>
              <button onClick={closeDeleteModal} className="ml-auto w-9 h-9 rounded-xl bg-white/10 border border-white/20 text-white/80 hover:bg-white/20 hover:text-white flex items-center justify-center transition-all">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-8">
              <div className="mb-6 p-5 rounded-xl bg-[#fef2f2] border-2 border-[#fecaca]">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#dc2626]/10 border-2 border-[#dc2626]/20 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-exclamation-triangle text-[#dc2626] text-base" />
                  </div>
                  <div>
                    <p className="text-[11px] text-[#b91c1c] font-syne font-extrabold uppercase tracking-wider">Confirm Deletion</p>
                    <p className="text-sm text-[#0f172a] font-extrabold mt-2">{deleteUserName}</p>
                    <p className="text-[10px] text-[#475569] font-mono font-bold mt-1.5">This will permanently delete the user account. The lead will be unlinked and remain in the system. All user data including trades, deposits, and documents will be removed.</p>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-3">
                  <i className="fas fa-lock mr-1.5 text-[#dc2626]" />SECURITY PIN
                </label>
                <input
                  type="password"
                  value={deletePin}
                  onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="• • • •"
                  maxLength={4}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-4 text-[#0f172a] text-lg font-mono font-extrabold outline-none focus:border-[#dc2626] focus:bg-white transition-all placeholder:text-[#cbd5e1] tracking-[0.5em] text-center"
                  onKeyDown={(e) => { if (e.key === 'Enter' && deletePin.length === 4) handleDeleteUser(); }}
                  autoFocus
                />
                <p className="text-[9px] text-[#94a3b8] font-mono font-bold mt-2 text-center">Enter 4-digit security code to authorize deletion</p>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={closeDeleteModal} 
                  className="flex-1 px-5 py-3.5 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] hover:border-[#cbd5e1] transition-all"
                >
                  CANCEL
                </button>
                <button 
                  onClick={handleDeleteUser} 
                  disabled={deletingUser || deletePin.length < 4} 
                  className="flex-[2] px-5 py-3.5 rounded-xl bg-[#dc2626] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[3px] border-[#b91c1c] flex items-center justify-center gap-2"
                >
                  {deletingUser ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="fas fa-trash-alt text-sm" />
                  )}
                  {deletingUser ? 'DELETING...' : 'DELETE USER'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UsersManagement;

